<script setup lang="ts">
  import { SchemeType } from '@/shared/types/api.types'
  import LinkQC from '@qicgroupdoha/qic-connect-ds/components/LinkQC'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'

  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { COVER_DATA } from '../../FlowSelectPlanWidget.constants'

  interface Props {
    plansSchemeTypeCodes: SchemeType[]
  }

  interface Emits {
    (e: 'click-learn-more'): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()

  const { t } = useI18n()

  const hasType = (type: SchemeType) => props.plansSchemeTypeCodes.includes(type)

  const isWorldwide = computed(() => hasType(SchemeType.Worldwide))
  const isRegional = computed(() => hasType(SchemeType.Regional))

  const annotation = computed(() => {
    if (isWorldwide.value)
      return t('outbound.select_plan.insurance_values.covered.annottation.ww_wwexcl')

    if (isRegional.value)
      return t('outbound.select_plan.insurance_values.covered.annottation.wwexcl_regional')

    return ''
  })
</script>

<template>
  <div>
    <h2 class="heading-4">{{ $t('outbound.select_plan.insurance_values.covered.header') }}</h2>
    <div class="text-regular plan-covers__description">
      {{ annotation }}
      <LinkQC :underline="false" @click="emit('click-learn-more')">{{
        $t('qic.common.learn_more')
      }}</LinkQC>
    </div>
    <div class="plan-covers__list">
      <div v-for="item in COVER_DATA" :key="item.title" class="plan-covers__item">
        <div class="plan-covers__item-title heading-6">
          {{ $t(item.title) }}
          <span v-if="item.annotation" class="plan-covers__item-annotation text-regular">{{
            $t(item.annotation)
          }}</span>
        </div>
        <ul>
          <li v-for="data in item.data" :key="data.text" class="plan-covers__item-content">
            <div class="plan-covers__item-text">
              <div>
                <div class="plan-covers__item-text-inner text-regular">
                  {{ $t(data.text) }}
                  <IconQC
                    v-if="data.tooltip"
                    v-tooltip-qc="{
                      title: $t(data.tooltip),
                    }"
                    color="var(--colorIcnSrfTertiary)"
                    name="info"
                    size="sm"
                  />
                </div>
                <div
                  v-if="data.annotation && isRegional"
                  class="plan-covers__item-annotation text-small-regular"
                >
                  {{ $t(data.annotation) }}
                </div>
              </div>
            </div>
            <div class="plan-covers__item-description text-regular">
              {{ $t(data.amount) }}
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @mixin border {
    content: '';
    position: absolute;
    background-color: var(--colorBrdrSrfSecondary);
  }

  @mixin border-bottom {
    /* stylelint-disable-next-line */
    @media (min-width: 768px) {
      &:not(:nth-last-child(-n + 2))::before {
        @include border;

        bottom: 0;
        left: 0;
        height: 1px;
        width: 100%;

        @content;
      }
    }
  }

  @mixin bottom-border-odd {
    @include border-bottom {
      width: calc(100% - var(--padding));
    }
  }

  @mixin bottom-border-even {
    @include border-bottom {
      inset-inline-start: var(--padding);
      width: calc(100% - var(--padding));
    }
  }

  @mixin right-border {
    /* stylelint-disable-next-line */
    @media (min-width: 768px) {
      &::after {
        @include border;

        top: var(--padding);
        inset-inline-end: 0;
        width: 1px;
        height: calc(100% - var(--padding) * 2);
      }
    }
  }

  .plan-covers__description {
    margin-top: var(--spacing-2);
    color: var(--colorTxtSrfSecondary);

    /* stylelint-disable-next-line */
    @media (max-width: 768px) {
      position: relative;
      padding-bottom: var(--spacing-6);
      border-bottom: 1px solid var(--colorBrdrSrfSecondary);
    }
  }

  .plan-covers__item-title {
    padding-block: var(--spacing-2) var(--spacing-2-5);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2);
  }

  .plan-covers__list {
    --padding: var(--spacing-7);

    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    /* stylelint-disable-next-line */
    @media (max-width: 768px) {
      --padding: 0;

      flex-direction: column;
      gap: var(--spacing-5);
      padding-top: var(--spacing-6);
    }

    .plan-covers__item {
      position: relative;
      width: 50%;

      /* stylelint-disable-next-line */
      @media (max-width: 768px) {
        width: 100%;
      }

      &:nth-child(4n + 1) {
        /* qic-connect-ignore-next-line */
        padding-block: var(--padding);

        /* qic-connect-ignore-next-line */
        padding-inline-end: var(--padding);

        @include bottom-border-odd;
        @include right-border;
      }

      &:nth-child(4n + 2) {
        /* qic-connect-ignore-next-line */
        padding-inline-start: var(--padding);

        /* qic-connect-ignore-next-line */
        padding-block: var(--padding);

        @include bottom-border-even;
      }

      &:nth-child(4n + 3) {
        /* qic-connect-ignore-next-line */
        padding-inline-end: var(--padding);

        /* qic-connect-ignore-next-line */
        padding-block: var(--padding);

        @include right-border;
        @include bottom-border-odd;
      }

      &:nth-child(4n + 4) {
        /* qic-connect-ignore-next-line */
        padding-inline-start: var(--padding);

        /* qic-connect-ignore-next-line */
        padding-block: var(--padding);

        @include bottom-border-even;
      }
    }
  }

  .plan-covers__item-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-block: var(--spacing-2);
  }

  .plan-covers__item-text {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
  }

  .plan-covers__item-text-inner {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
  }

  .plan-covers__item-description {
    color: var(--colorTxtSrfSecondary);
  }

  .plan-covers__item-annotation {
    color: var(--colorTxtSrfSecondary);
  }
</style>
